
import api from '@/services/api';
import _ from 'underscore';
import {Module} from 'vuex';
import {RootState} from '@/types';
import { MaterialItem, IMaterialItem } from '@/classes/MaterialItem';
import { MaterialForm, IMaterialForm } from '@/classes/MaterialForm';
import { MaterialCategory, IMaterialCategory } from '@/classes/MaterialCategory';

export interface MaterialState {
    items: MaterialItem[];
    categories: MaterialCategory[];
    materialForms: MaterialForm[];
}

export const MaterialStore: Module<MaterialState, RootState> = {
    namespaced: true,

    state: {
        items: [],
        categories: [],
        materialForms: []
    },

    getters: {
        getCategoryById: (state) => (id: number): MaterialCategory | undefined => {
            const index: number = state.categories.findIndex((user: MaterialCategory) => user.id === Number(id));
            if (index !== -1) {
                return state.categories[index];
            } else {
                return undefined;
            }
        },
        getMaterialCategories: (state): MaterialCategory[] => {
            return state.categories;
        },
        getMaterialItemById: (state) => (id: number): MaterialItem | undefined => {
            const index: number = state.items.findIndex((item: MaterialItem) => item.id === Number(id));
            if (index !== -1) {
                return state.items[index];
            } else {
                return undefined;
            }
        },
        getMaterialItems: (state): MaterialItem[] => {
            const items: MaterialItem[] = state.items.filter((m: MaterialItem) => m.active);
            return items.sort((itemA: MaterialItem, itemB: MaterialItem) => {
                if (itemA.categoryName !== undefined && itemB.categoryName !== undefined
                    && itemA.listItem !== undefined && itemB.listItem !== undefined) {
                    return itemA.categoryName.localeCompare(itemB.categoryName) || itemA.listItem.localeCompare(itemB.listItem);
                } else {
                    return 0;
                }
            });
        },
        getMaterialForms: (state): MaterialForm[] => {
            return state.materialForms;
        },
        getMaterialFormById: (state) => (id: number): MaterialForm | undefined => {
            const index: number = state.materialForms.findIndex((item: MaterialForm) => item.id === Number(id));
            if (index !== -1) {
                return state.materialForms[index];
            } else {
                return undefined;
            }
        }
    },

    mutations: {
        setMaterialItems(state, items: MaterialItem[]) {
            state.items = items;
        },
        setMaterialCategories(state, categories: MaterialCategory[]) {
            state.categories = categories;
        },
        setMaterialForms(state, materialForms: MaterialForm[]) {
            state.materialForms = materialForms;
        }
    },

    actions: {
        loadMaterialCategories({ commit, state }, forceRefresh: boolean) {
            return new Promise((resolve, reject) => {
                if (state.categories.length === 0 || forceRefresh) {
                    api().get('material/categories')
                    .then((response) => {
                        if (response.data.success) {
                            const categoriesJSON = response.data.categories;
                            const categories: MaterialCategory[] = [];
                            categoriesJSON.forEach((element: any) => {
                                const materialCategory: MaterialCategory = new MaterialCategory(element as IMaterialCategory);
                                categories.push(materialCategory);
                            });
                            commit('setMaterialCategories', categories);
                        }
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                    });
                } else {
                    resolve();
                }
            });
        },
        loadMaterialItems({ commit, state, dispatch }, forceRefresh: boolean) {
            return new Promise((resolve, reject) => {
                dispatch('loadMaterialCategories')
                .then(() => {
                    if (state.items.length === 0 || forceRefresh) {
                        api().get('material/items')
                        .then((response) => {
                            if (response.data.success) {
                                const itemsJSON = response.data.items;
                                const items: MaterialItem[] = [];
                                itemsJSON.forEach((element: any) => {
                                    const materialItem: MaterialItem = new MaterialItem(element as IMaterialItem);
                                    items.push(materialItem);
                                });
                                commit('setMaterialItems', items);
                            }
                            resolve();
                        })
                        .catch((e) => {
                            reject(e);
                        });
                    } else {
                        resolve();
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        addMaterialItem({ dispatch }, item: MaterialItem) {
            return new Promise((resolve, reject) => {
                api().post('material/item', {item})
                .then((response) => {
                    if (response.data.success) {
                        dispatch('loadMaterialItems', true)
                        .then(() => {
                            resolve(response.data);
                        });
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        editMaterialItem({ dispatch }, item: MaterialItem) {
            return new Promise((resolve, reject) => {
                api().patch('material/item', {item})
                .then((response) => {
                    if (response.data.success) {
                        dispatch('loadMaterialItems', true)
                        .then(() => {
                            resolve(response.data);
                        });
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        deleteMaterialItem({ dispatch }, item: MaterialItem) {
            return new Promise((resolve, reject) => {
                api().delete('material/item', { data: {item} })
                .then((response) => {
                    if (response.data.success) {
                        dispatch('loadMaterialItems', true)
                        .then(() => {
                            resolve(response.data);
                        });
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        loadMaterialForms({ commit, state, dispatch }, forceRefresh: boolean) {
            return new Promise((resolve, reject) => {
                if (state.materialForms.length === 0 || forceRefresh) {
                    api().get('material/forms')
                    .then((response) => {
                        if (response.data.success) {
                            const formsJSON = response.data.forms;
                            const forms: MaterialForm[] = [];
                            formsJSON.forEach((element: any) => {
                                const materialForm: MaterialForm = new MaterialForm(element as IMaterialForm);
                                forms.push(materialForm);
                            });
                            commit('setMaterialForms', forms);
                            dispatch('loadMaterialItems');
                        }
                        resolve();
                    })
                    .catch((e) => {
                        reject(e);
                    });
                } else {
                    resolve();
                }
            });
        },
        addMaterialForm({ dispatch }, form: MaterialForm) {
            return new Promise((resolve, reject) => {
                api().post('material/form', {form})
                .then((response) => {
                    if (response.data.success) {
                        dispatch('loadMaterialForms', true)
                        .then(() => {
                            resolve(response.data);
                        });
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        },
        editMaterialForm({ dispatch }, form: MaterialForm) {
            return new Promise((resolve, reject) => {
                api().patch('material/form', {form})
                .then((response) => {
                    if (response.data.success) {
                        dispatch('loadMaterialForms', true)
                        .then(() => {
                            resolve(response.data);
                        });
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((e) => {
                    reject(e);
                });
            });
        }
    }
};
